import { API_ROUTES } from '@/api/apiRoutes';
import { safeAxios } from '@/services/safeAxios';
import { getMimeTypeFromExtension } from '@/utils/fileTypes';
import { validateFile } from '@/utils/nodeInputValidation';
import { useWorkflowStore } from '@/state/workflow.state';
import { RunType } from '@/enums/run-type.enum';
import type { MediaAsset, UploadAssetResponse } from '@/types/api/assets';

/**
 * A single upload cannot run forever. The shared axios instance has no timeout, so a stalled
 * connection would hang the promise, and the compositor add path (which runs uploads one at a time
 * and blocks new ones until this settles) would stay stuck until reload.
 *
 * This caps the whole request, not idle time, so a slow-but-alive upload that runs past it is cut
 * off too. Video and audio files are much larger than images, so the cap is generous enough for a
 * large clip on a slow connection; a genuinely stalled request still fails and releases the lock.
 */
const UPLOAD_TIMEOUT_MS = 300_000;

/**
 * Uploads one file to the assets endpoint and resolves with its {@link MediaAsset}.
 *
 * `useUploadMediaAsset` reports its result through a callback, which is hard to await when you need
 * to upload several files, wait for all of them, and then add them together as one undo step. This
 * function returns a promise instead, so an ordered batch is easy to build on top of it.
 *
 * It does not check whether the browser can play a dropped video (`probeVideoFileBrowserSupport`).
 * A caller that accepts video must run that check first.
 *
 * @throws if the request fails or the response is not a valid asset.
 */
export async function uploadAssetFile(file: File, runType: RunType): Promise<MediaAsset> {
  const formData = new FormData();
  formData.append('file', file);
  const mimeType = file.type || getMimeTypeFromExtension(file.name);
  if (mimeType) {
    formData.append('type', mimeType);
  }

  const response = await safeAxios.post<UploadAssetResponse>(API_ROUTES.assets.uploadAsset(), formData, {
    timeout: UPLOAD_TIMEOUT_MS,
    params: { runType },
  });
  useWorkflowStore.getState().mergeResolvedAssets(response.data.resolvedAssets ?? {});
  if (!validateFile(response.data.asset)) {
    throw new Error('Invalid asset upload response: ' + JSON.stringify(response.data.asset));
  }
  return { ...response.data.asset, name: file.name };
}

export type UploadAssetResult =
  { status: 'fulfilled'; file: File; asset: MediaAsset } | { status: 'rejected'; file: File; reason: unknown };

/**
 * Uploads several files at once, keeping input order, and reports each file's outcome on its own.
 * One failed upload never throws away the files that succeeded, so the caller can add the good ones
 * and tell the user which files failed.
 */
export async function uploadAssetFiles(files: File[], runType: RunType): Promise<UploadAssetResult[]> {
  const settled = await Promise.allSettled(files.map((file) => uploadAssetFile(file, runType)));
  return settled.map((result, index) => {
    const file = files[index];
    return result.status === 'fulfilled'
      ? { status: 'fulfilled' as const, file, asset: result.value }
      : { status: 'rejected' as const, file, reason: result.reason };
  });
}
